import { PATH_DASHBOARD } from 'routes/paths';
import * as Yup from 'yup';
import {
  BLOG_EDITOR_FIELDS,
  CREATE_PASSWORD_FIELD,
  DATA_TYPES,
  DYNAMIC_FORM_DEFAULT_KEYS,
  ENQUIRY_STATUS,
  MODULE_CATEGORIES,
  QUOTATION_ENQUIRY_TYPES,
  SEO_DETAIL_FIELDS,
  STATUS,
} from './constants';

export function convertToFormattedString(input = '') {
  if (!input) return '';
  // Split the input string by underscores and capitalize each word.
  const words = input
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  // Join the capitalized words with spaces to create the formatted string.
  return words.join(' ');
}

export function getFormattedModuleLabel(input = '', divider = ' ') {
  if (!input) return '';
  // Split the input string by given divider and capitalize each word.
  const words = input
    .split(divider)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  // Join the capitalized words with spaces to create the formatted string.
  return words.join(' ');
}

export const convertToConstantCase = (input) => {
  return input
    ?.trim()
    ?.replace(/\s+/g, '_') // replace one or more spaces with underscore
    ?.replace(/[^a-zA-Z0-9_]/g, '') // optionally remove special characters
    ?.toUpperCase();
};

export const convertCamelToCapitalizeCase = (text) => {
  if (typeof text !== 'string') return '';
  return text
    .replace(/([A-Z])/g, ' $1') // insert space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()) // capitalize first letter
    .trim(); // remove leading/trailing spaces
};

export const getTruncateText = (text, maxLength = 60) => {
  if (typeof text !== 'string') return text;
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/* eslint-disable */

export const getTotalCount = (search, status, counts) => {
  if (search) {
    return counts?.current;
  } else if (status === 'all') {
    return counts?.current;
  } else if (status === STATUS.ACTIVE) {
    return counts?.active;
  } else if (status === STATUS.INACTIVE) {
    return counts?.inactive;
  } else if (status === STATUS.EXPIRED) {
    return counts?.expired;
  }
  return counts?.total;
};

export const getColorByStatus = (status) => {
  switch (status) {
    case STATUS.ACTIVE:
      return 'success';
    case STATUS.INACTIVE:
      return 'error';
    case STATUS.EXPIRED:
      return 'error';
    default:
      return 'default';
  }
};

export const getColorByEnquiryStatus = (status) => {
  switch (status) {
    case ENQUIRY_STATUS.NEW:
      return 'info';
    case QUOTATION_ENQUIRY_TYPES.NEW_QUOTATION:
      return 'info';
    case ENQUIRY_STATUS.CLOSURE:
      return 'success';
    case ENQUIRY_STATUS.HIRED:
      return 'success';
    case QUOTATION_ENQUIRY_TYPES.QUOTATION_CONFIRMED:
      return 'success';
    case ENQUIRY_STATUS.FOLLOW_UP:
      return 'warning';
    case ENQUIRY_STATUS.SHORTLIST:
      return 'warning';
    case ENQUIRY_STATUS.REJECT:
      return 'error';
    case ENQUIRY_STATUS.SPAM:
      return 'error';
    case ENQUIRY_STATUS.DECLINED:
      return 'error';
    default:
      return 'default';
  }
};

export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
};

export const decodeErrorMessage = (error = null) => {
  if (error) {
    const errorMessage = error?.response?.data?.message;

    if (Array.isArray(errorMessage)) {
      const firstError = errorMessage[0];
      const fieldName = Object.keys(firstError)[0];
      const fieldValue = Object.values(firstError)[0];
      return `${fieldName} : ${fieldValue}`;
    }

    return errorMessage;
  }

  return error;
};

export const generateSlug = (title) => {
  // Implement your slug generation logic here, e.g., converting to lowercase and replacing spaces with hyphens
  return title
    .replaceAll(/[^a-zA-Z0-9\s]/g, ' ')
    .trim()
    .replaceAll(' ', '-')
    .toLowerCase();
};

// Cap dimensions for web uploads; quality is tuned via binary search under sizeCapRatio.
const AVIF_CONVERT_DEFAULT_MAX = 1920;
// Large uploads are compressed toward this size when possible (e.g. 5 MB → ~512 KB).
const AVIF_UPLOAD_SOFT_CAP_BYTES = 512 * 1024;
const AVIF_ENCODE_SPEED = 9;

let avifEncodeModulePromise;

const getAvifEncoder = () => {
  if (!avifEncodeModulePromise) {
    avifEncodeModulePromise = import('@jsquash/avif').then((mod) => mod.encode);
  }
  return avifEncodeModulePromise;
};

const fileToScaledImageData = async (file, maxWidth, maxHeight) => {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    bitmap.close?.();
    throw new Error('Canvas is not available for image conversion.');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  return ctx.getImageData(0, 0, width, height);
};

const avifEncodeFromImageData = async (imageData, quality) => {
  const encode = await getAvifEncoder();
  const q = Math.min(100, Math.max(1, Math.round(quality)));
  const buffer = await encode(imageData, { quality: q, speed: AVIF_ENCODE_SPEED });
  return new Blob([buffer], { type: 'image/avif' });
};

const getUploadTargetBytes = (originalSize, sizeCapRatio, maxTargetBytes) => {
  let target = Math.max(1, Math.floor(originalSize * sizeCapRatio));
  if (originalSize > AVIF_UPLOAD_SOFT_CAP_BYTES) {
    const cap = maxTargetBytes ?? AVIF_UPLOAD_SOFT_CAP_BYTES;
    target = Math.min(target, cap);
  }
  return target;
};

/**
 * Converts raster images to AVIF using a real AVIF encoder (@jsquash/avif).
 * Picks the highest quality in [minQuality, maxQuality] such that output size is
 * <= original size * sizeCapRatio when possible; otherwise uses the smallest encode.
 *
 * @param {File|Blob} file
 * @param {object} [options]
 * @param {number} [options.maxWidth]
 * @param {number} [options.maxHeight]
 * @param {string} [options.outputType] 'file' (default) or 'blob'
 * @param {number} [options.minQuality] lowest quality to try (default 38)
 * @param {number} [options.maxQuality] highest quality to try (default 85). Legacy `quality` option is treated as maxQuality.
 * @param {number} [options.sizeCapRatio] max output / input size (default 1)
 * @param {number} [options.maxTargetBytes] hard target for large files (default 512 KB when input is larger)
 */
export const convertImageToAvif = async (file, options = {}) => {
  const {
    maxWidth = AVIF_CONVERT_DEFAULT_MAX,
    maxHeight = AVIF_CONVERT_DEFAULT_MAX,
    outputType = 'file',
    minQuality = 38,
    maxQuality: maxQualityOpt,
    quality: legacyQualityCap,
    sizeCapRatio = 1,
    maxTargetBytes,
  } = options;

  const maxQuality = Math.min(100, Math.max(1, maxQualityOpt ?? legacyQualityCap ?? 85));
  const minQ = Math.min(maxQuality, Math.max(1, Math.floor(minQuality)));

  if (!file || typeof file !== 'object') {
    throw new Error('Invalid file input.');
  }

  const looksLikeImage =
    file.type?.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|tiff?)$/i.test(file.name || '');
  if (!looksLikeImage) {
    return file;
  }

  const isAvif =
    file.type === 'image/avif' || /\.avif$/i.test(typeof file.name === 'string' ? file.name : '');
  if (isAvif && file.size <= AVIF_UPLOAD_SOFT_CAP_BYTES) {
    return file;
  }

  const originalSize = file.size;
  const targetBytes = getUploadTargetBytes(originalSize, sizeCapRatio, maxTargetBytes);

  const imageData = await fileToScaledImageData(file, maxWidth, maxHeight);

  let bestUnderCap = null;
  let smallestBlob = null;
  let lo = minQ;
  let hi = maxQuality;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    // eslint-disable-next-line no-await-in-loop
    const blob = await avifEncodeFromImageData(imageData, mid);
    if (!smallestBlob || blob.size < smallestBlob.size) {
      smallestBlob = blob;
    }
    if (blob.size <= targetBytes) {
      bestUnderCap = blob;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  const bestBlob = bestUnderCap ?? smallestBlob;
  if (!bestBlob) {
    throw new Error('AVIF conversion failed.');
  }

  const rawName = typeof file.name === 'string' && file.name ? file.name : 'image';
  const avifName = `${rawName.replace(/\.[^.]+$/, '')}.avif`;

  if (outputType === 'blob') {
    return bestBlob;
  }

  return new File([bestBlob], avifName, {
    type: 'image/avif',
    lastModified: Date.now(),
  });
};

export const getFormattedImageURL = (image = '', logoPath = '') => {
  if (image) {
    const s3BucketFolder = sessionStorage.getItem('bucketName') || "";

    const imageName = image?.split('/')?.pop();
    const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || "https://technolitics-s3-bucket.s3.ap-south-1.amazonaws.com";
    // const S3_BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME;
    const S3_BUCKET_NAME = logoPath && logoPath !== "" ? logoPath : (s3BucketFolder || "maira-s3-bucket");
    const formattedImage = `${S3_BASE_URL}/${S3_BUCKET_NAME}/${imageName}`;

    return formattedImage;
  }
  return image;
};

export const getSlugByPathname = (path) => {
  if (path) {
    const dashboardIndex = path.indexOf('/dashboard/');
    if (dashboardIndex === -1) return null;

    const after = path.slice(dashboardIndex + '/dashboard/'.length);
    return after.split('/')[0]; // Safe extraction
  }
  return null;
};

export function formatModuleName(moduleKey) {
  return moduleKey
    .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Convert the first character to uppercase
}

export const getModuleBySlug = (modules = [], slug = '') =>
  modules?.length > 0 ? modules?.find((item) => item?.slug === slug) : null;

export const checkIfEnquiryModule = (moduleCategory = '') =>
  moduleCategory ? (moduleCategory === MODULE_CATEGORIES.ENQUIRIES || moduleCategory === MODULE_CATEGORIES.INBOX) : false;

export const checkIfRightsAssignedTo = (userRights = null, right = '') =>
  userRights ? userRights[right] : false;

export const getDynamicFieldLabel = (dynamicFields = [], fieldSlug = '') =>
  dynamicFields?.length > 0 ? dynamicFields?.find((item) => item?.slug === fieldSlug)?.label : null;

export const getUltimateNavigation = (navConfig = {}) => {
  if (
    navConfig &&
    Array.isArray(navConfig?.navigation) &&
    navConfig?.customizableFields.length > 0
  ) {
    const customizableSchemaByUserNavigation = {
      subheader: MODULE_CATEGORIES.CONFIGURATION,
      items: [{ title: 'Field Customization', path: PATH_DASHBOARD.configuration.dynamicForm }],
    };

    const hasConfiguration = navConfig?.navigation?.find(
      (category) => category?.subheader === MODULE_CATEGORIES.CONFIGURATION
    );
    const customizationIncludedNavConfig = hasConfiguration
      ? navConfig?.navigation?.map((category) =>
        category?.subheader === MODULE_CATEGORIES.CONFIGURATION
          ? {
            ...category,
            items: [...category?.items, customizableSchemaByUserNavigation.items[0]],
          }
          : category
      )
      : [...navConfig?.navigation, customizableSchemaByUserNavigation];

    return customizationIncludedNavConfig;
  }
  return navConfig?.navigation || [];
};
export const getInitialValueForDynamicField = (dataType, userName) => {
  const stringTypes = new Set([
    DATA_TYPES.PLAIN_TEXT,
    DATA_TYPES.LONG_TEXT,
    DATA_TYPES.RICH_TEXT,
    DATA_TYPES.EMAIL,
    DATA_TYPES.NUMBER,
    DATA_TYPES.AVATAR_IMAGE,
    DATA_TYPES.COVER_IMAGE,
    DATA_TYPES.LINK,
    DATA_TYPES.DROPDOWN,
    DATA_TYPES.RADIO,
    DATA_TYPES.PASSWORD,
  ]);
  const arrayTypes = new Set([
    DATA_TYPES.ARRAY,
    DATA_TYPES.TAGS,
    DATA_TYPES.CHECKBOX,
    DATA_TYPES.MULTI_SELECION_DROPDOWN,
    DATA_TYPES.MULTIPLE_IMAGES,
  ]);

  if (stringTypes.has(dataType)) return '';
  if (arrayTypes.has(dataType)) return [];
  if (dataType === DATA_TYPES.TOGGLE) return false;
  if (dataType === DATA_TYPES.DATE) return new Date();
  if (dataType === DATA_TYPES.BLOG_EDITOR) return [{ description: '' }];
  if (dataType === DATA_TYPES.VARIENT_EDITOR) return [{ description: '' }];
  if (dataType === DATA_TYPES.MULTI_LINK) return [{ link: '' }];
  if (dataType === DATA_TYPES.MULTIPLE_FILE) return [{ file: '' }];
  if (dataType === DATA_TYPES.SEO_CONFIG)
    return {
      [SEO_DETAIL_FIELDS.SEO_TITLE]: '',
      [SEO_DETAIL_FIELDS.SEO_SLUG]: '',
      [SEO_DETAIL_FIELDS.SEO_DESCRIPTION]: '',
      [SEO_DETAIL_FIELDS.SEO_IMAGE]: '',
      [SEO_DETAIL_FIELDS.SEO_TAGS]: userName ? [userName] : [],
    };
  if (dataType === DATA_TYPES.MULTI_OBJECT_ID) return [];

  return null;
};

export const getDynamicFormSchema = ({
  schema: dynamicSchemaShape = {},
  field = null,
  dependentField = null,
}) => {
  const { key, label, isRequired, dataType } = field;

  switch (dataType) {
    case DATA_TYPES.PLAIN_TEXT:
    case DATA_TYPES.LONG_TEXT:
    case DATA_TYPES.RICH_TEXT:
    case DATA_TYPES.AVATAR_IMAGE:
    case DATA_TYPES.COVER_IMAGE: {
      let rule = Yup.string();
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.string(),
          });
        } else rule = rule.required(`${label} is required`);
      }
      if (field?.validations?.minLength)
        rule = rule.min(
          field?.validations?.minLength,
          `${label} must be at least ${field?.validations?.minLength} characters`
        );
      if (field?.validations?.maxLength)
        rule = rule.max(
          field?.validations?.maxLength,
          `${label} must be at most ${field?.validations?.maxLength} characters`
        );
      if (field?.validations?.pattern)
        rule = rule.matches(new RegExp(field.pattern), `${label} format is invalid`);
      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.EMAIL: {
      let rule = Yup.string().email('Email must be a valid email address').nullable();
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.string().email('Email must be a valid email address').nullable(),
          });
        } else rule = rule.required(`${label} is required`);
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.NUMBER:
    case DATA_TYPES.CURRENCY: {
      let rule = Yup.number().typeError(`${label} must be a number`);

      if (field?.validations?.minLength)
        rule = rule.min(
          field?.validations?.minLength,
          `${label} must be at least ${field?.validations?.minLength}`
        );
      if (field?.validations?.maxLength)
        rule = rule.max(
          field?.validations?.maxLength,
          `${label} must be at most ${field?.validations?.maxLength}`
        );
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.number().typeError(`${label} must be a number`),
          });
        } else rule = rule.required(`${label} is required`);
      }
      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.TOGGLE: {
      let rule = Yup.boolean();
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.boolean(),
          });
        } else rule = rule.required(`${label} is required`);
      }
      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.LINK: {
      let rule = Yup.string().url(`${label} must be a valid URL`);
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.string().url(`${label} must be a valid URL`),
          });
        } else rule = rule.required(`${label} is required`);
      }
      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.DROPDOWN:
    case DATA_TYPES.RADIO: {
      let rule = Yup.string();

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.string(),
          });
        } else rule = rule.required(`${label} is required`);

        if (field?.options?.length) {
          rule = rule.oneOf(field?.options, `${label} must be one of the allowed options`);
        }
      }
      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.DATE: {
      let rule = Yup.date();
      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.required(`${label} is required`),
            otherwise: Yup.date(),
          });
        } else rule = rule.required(`${label} is required`);
      }

      dynamicSchemaShape[key] = rule.nullable();
      break;
    }

    case DATA_TYPES.TAGS: {
      let rule = Yup.array().of(Yup.string().trim());

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.min(1, `${label} must have at least one tag`),
            otherwise: Yup.array(),
          });
        } else rule = rule.min(1, `${label} must have at least one tag`);
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.ARRAY:
    case DATA_TYPES.CHECKBOX:
    case DATA_TYPES.MULTI_SELECION_DROPDOWN:
    case DATA_TYPES.MULTIPLE_IMAGES: {
      let rule = Yup.array();

      // If allowed options are defined, ensure each value is in them
      if (field?.options?.length) {
        rule = rule.of(Yup.string().oneOf(field?.options));
      }

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.min(1, `${label} must have at least one selected option`),
            otherwise: Yup.array(),
          });
        } else rule = rule.min(1, `${label} must have at least one selected option`);
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.BLOG_EDITOR: {
      const { SINGLE_IMAGE, MULTIPLE_IMAGES, YOUTUBE, INSTAGRAM, FACEBOOK, TWITTER, BUTTON } =
        BLOG_EDITOR_FIELDS;
      const defaultType = Yup.array().of(
        Yup.object().shape({
          [SINGLE_IMAGE]: Yup.object().shape({
            image: Yup.string(),
            altTag: Yup.string(),
            hyperLink: Yup.string().url(
              `${convertCamelToCapitalizeCase(SINGLE_IMAGE)} hyperlink must be a valid URL`
            ),
          }),
          [MULTIPLE_IMAGES]: Yup.array().of(Yup.string()),
          [YOUTUBE]: Yup.string().url(
            `${convertCamelToCapitalizeCase(YOUTUBE)} must be a valid URL`
          ),
          [INSTAGRAM]: Yup.string().url(
            `${convertCamelToCapitalizeCase(INSTAGRAM)} must be a valid URL`
          ),
          [FACEBOOK]: Yup.string().url(
            `${convertCamelToCapitalizeCase(FACEBOOK)} must be a valid URL`
          ),
          [TWITTER]: Yup.string().url(
            `${convertCamelToCapitalizeCase(TWITTER)} must be a valid URL`
          ),
          [BUTTON]: Yup.object().shape({
            title: Yup.string(),
            hyperLink: Yup.string().url(
              `${convertCamelToCapitalizeCase(BUTTON)} link must be a valid URL`
            ),
          }),
        })
      );
      let rule = defaultType;

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.min(1, `${label} must have at least one selected option`),
            otherwise: defaultType,
          });
        } else rule = rule.min(1, `${label} must have at least one selected option`);
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.SEO_CONFIG: {
      const { SEO_TITLE, SEO_SLUG, SEO_TAGS, SEO_IMAGE, SEO_DESCRIPTION } = SEO_DETAIL_FIELDS;
      //fields with default schema type
      const defaultType = Yup.object().shape({
        [SEO_TITLE]: Yup.string(),
        [SEO_SLUG]: Yup.string(),
        [SEO_DESCRIPTION]: Yup.string(),
        [SEO_TAGS]: Yup.array().of(Yup.string()),
        [SEO_IMAGE]: Yup.string(),
      });
      // fields with required validation
      const requiredType = Yup.object().shape({
        [SEO_TITLE]: Yup.string().required(
          `${convertCamelToCapitalizeCase(SEO_TITLE)} is required`
        ),
        [SEO_SLUG]: Yup.string().required(`${convertCamelToCapitalizeCase(SEO_SLUG)} is required`),
        [SEO_DESCRIPTION]: Yup.string().required(
          `${convertCamelToCapitalizeCase(SEO_DESCRIPTION)} is required`
        ),
        [SEO_TAGS]: Yup.array().of(Yup.string()),
        [SEO_IMAGE]: Yup.string().required(
          `${convertCamelToCapitalizeCase(SEO_IMAGE)} is required`
        ),
      });

      let rule = defaultType;

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: requiredType,
            otherwise: defaultType,
          });
        } else rule = requiredType;
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.PASSWORD: {
      // first create a rule for createPassword
      dynamicSchemaShape[CREATE_PASSWORD_FIELD] = Yup.string().when(
        DYNAMIC_FORM_DEFAULT_KEYS.STAFF_LOGIN_RIGHT,
        {
          is: (isEdit, isLoginAllowed) => !isEdit && isLoginAllowed === true,
          then: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password is required'),
          otherwise: Yup.string().notRequired(),
        }
      );

      let rule = Yup.string().when(DYNAMIC_FORM_DEFAULT_KEYS.STAFF_LOGIN_RIGHT, {
        is: (isEdit, isLoginAllowed) => !isEdit && isLoginAllowed === true,
        then: Yup.string()
          .required('Confirm password is required')
          .oneOf([Yup.ref(CREATE_PASSWORD_FIELD), null], 'Passwords must match'),
        otherwise: Yup.string().notRequired(),
      });

      dynamicSchemaShape[key] = rule;
      break;
    }

    case DATA_TYPES.MULTI_OBJECT_ID: {
      let rule = Yup.array().of(
        Yup.string().matches(/^[0-9a-fA-F]{24}$/, `${label} must contain valid selections`)
      );

      if (isRequired) {
        if (dependentField?.key) {
          rule = rule.when(dependentField?.key, {
            is: (dependentValue) =>
              !!dependentValue && dependentValue === field?.dependency?.dependValue,
            then: rule.min(1, `${label} must have at least one selected option`),
            otherwise: Yup.array(),
          });
        } else {
          rule = rule.min(1, `${label} must have at least one selected option`);
        }
      }

      dynamicSchemaShape[key] = rule;
      break;
    }

    default:
      dynamicSchemaShape[key] = Yup.mixed();
  }
};
